import { useState, useEffect } from "react";
import { LightPageLayout } from "../components/Layout";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Search, MapPin, Award, GraduationCap, Building2, ExternalLink,
  Users, Filter, X, Briefcase, CheckCircle
} from "lucide-react";

const AlumniDirectory = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: "", industry: "", certification: "", search: "" });
  const [filterOptions, setFilterOptions] = useState({ states: [], industries: [], certifications: [] });
  const [totalCount, setTotalCount] = useState(0);

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state) params.set("state", filters.state);
      if (filters.industry) params.set("industry", filters.industry);
      if (filters.certification) params.set("certification", filters.certification);
      if (filters.search) params.set("search", filters.search);

      const res = await fetch(`${API}/alumni/directory?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAlumni(data.alumni || []);
        setTotalCount(data.total || 0);
        // Only update filter options if no filters applied (to preserve full list)
        if (!filters.state && !filters.industry && !filters.certification && !filters.search) {
          setFilterOptions(data.filter_options || { states: [], industries: [], certifications: [] });
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Load filter options on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await fetch(`${API}/alumni/directory/filters`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setFilterOptions(data);
        }
      } catch (err) { console.error(err); }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchAlumni();
  }, [filters.state, filters.industry, filters.certification]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => fetchAlumni(), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const hasActiveFilters = filters.state || filters.industry || filters.certification || filters.search;

  const clearFilters = () => {
    setFilters({ state: "", industry: "", certification: "", search: "" });
  };

  return (
    <LightPageLayout
      title="Alumni Directory"
      subtitle="Connect with Aspiritech Academy graduates"
    >
      <div className="max-w-6xl mx-auto space-y-6" data-testid="alumni-directory">
        {/* Stats bar */}
        <div className="flex items-center gap-6 px-1">
          <div className="flex items-center gap-2 text-slate-600">
            <Users className="w-5 h-5 text-[#1E3A8A]" />
            <span className="text-sm font-medium">{totalCount} Alumni</span>
          </div>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-red-500 hover:underline" data-testid="clear-filters-btn">
              <X className="w-3.5 h-3.5" /> Clear filters
            </button>
          )}
        </div>

        {/* Filters */}
        <Card className="card-light">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-slate-500 text-sm font-medium">
                <Filter className="w-4 h-4" /> Filters
              </div>

              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  className="input-light pl-10 h-9 text-sm"
                  placeholder="Search by name, industry, employer..."
                  data-testid="alumni-search-input"
                />
              </div>

              <Select value={filters.state || "all"} onValueChange={(val) => setFilters(f => ({ ...f, state: val === "all" ? "" : val }))}>
                <SelectTrigger className="w-40 h-9 bg-white border-slate-200 text-sm" data-testid="filter-state">
                  <MapPin className="w-3.5 h-3.5 text-slate-400 mr-1" />
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All States</SelectItem>
                  {filterOptions.states?.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.industry || "all"} onValueChange={(val) => setFilters(f => ({ ...f, industry: val === "all" ? "" : val }))}>
                <SelectTrigger className="w-52 h-9 bg-white border-slate-200 text-sm" data-testid="filter-industry">
                  <Briefcase className="w-3.5 h-3.5 text-slate-400 mr-1" />
                  <SelectValue placeholder="Industry / Program" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Industries</SelectItem>
                  {filterOptions.industries?.map(i => (
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.certification || "all"} onValueChange={(val) => setFilters(f => ({ ...f, certification: val === "all" ? "" : val }))}>
                <SelectTrigger className="w-52 h-9 bg-white border-slate-200 text-sm" data-testid="filter-certification">
                  <Award className="w-3.5 h-3.5 text-slate-400 mr-1" />
                  <SelectValue placeholder="Certification" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Certifications</SelectItem>
                  {filterOptions.certifications?.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#FFD507] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && alumni.length === 0 && (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No alumni found</h3>
            <p className="text-sm text-slate-500 mt-1">
              {hasActiveFilters ? "Try adjusting your filters" : "Alumni will appear here as participants complete their training"}
            </p>
            {hasActiveFilters && (
              <Button onClick={clearFilters} variant="outline" className="btn-outline mt-4" data-testid="empty-clear-filters">
                Clear Filters
              </Button>
            )}
          </div>
        )}

        {/* Alumni grid */}
        {!loading && alumni.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="alumni-grid">
            {alumni.map((a) => (
              <Card key={a.user_id} className="card-light hover:shadow-md transition-shadow" data-testid={`alumni-card-${a.user_id}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-start gap-3 mb-3">
                    {a.picture ? (
                      <img
                        src={a.picture}
                        alt={a.name || ""}
                        className="w-12 h-12 rounded-full object-cover border-2 border-slate-100 bg-slate-100"
                        onError={(e) => {
                          // Hide broken image and show initials fallback (the sibling div below).
                          e.currentTarget.style.display = "none";
                          if (e.currentTarget.nextSibling) {
                            e.currentTarget.nextSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0F1B3D] to-[#1E3A8A] items-center justify-center text-white text-lg font-bold flex-shrink-0"
                      style={{ display: a.picture ? "none" : "flex" }}
                      aria-hidden="true"
                    >
                      {a.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{a.name}</h3>
                      {a.industry && (
                        <p className="text-sm text-slate-500 truncate flex items-center gap-1">
                          <Briefcase className="w-3 h-3 flex-shrink-0" /> {a.industry}
                        </p>
                      )}
                      {(a.city || a.state) && (
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3 flex-shrink-0" /> {[a.city, a.state].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                    {a.is_certified && (
                      <div className="flex-shrink-0" title="Certified">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                    )}
                  </div>

                  {/* Employer */}
                  {a.post_training_employer && (
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="text-sm text-slate-600 truncate">{a.post_training_employer}</span>
                    </div>
                  )}

                  {/* Education */}
                  {a.education && (
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <GraduationCap className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="text-sm text-slate-600 truncate capitalize">{a.education.replace(/_/g, " ")}</span>
                    </div>
                  )}

                  {/* Certifications */}
                  {a.certifications?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {a.certifications.slice(0, 3).map((cert, i) => (
                        <Badge key={i} className="bg-[#FFD507]/10 text-[#1E3A8A] border-0 text-xs px-2 py-0.5">
                          {cert}
                        </Badge>
                      ))}
                      {a.certifications.length > 3 && (
                        <Badge className="bg-slate-100 text-slate-500 border-0 text-xs px-2 py-0.5">
                          +{a.certifications.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Skills */}
                  {a.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {a.skills.slice(0, 4).map((skill, i) => (
                        <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Links */}
                  {(a.linkedin_url || a.portfolio_url) && (
                    <div className="flex gap-2 pt-2 border-t border-slate-100">
                      {a.linkedin_url && (
                        <a href={a.linkedin_url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> LinkedIn
                        </a>
                      )}
                      {a.portfolio_url && (
                        <a href={a.portfolio_url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> Portfolio
                        </a>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </LightPageLayout>
  );
};

export default AlumniDirectory;
